import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { jobPostApi } from '@/_services/api/jobPost';
import ChannelSelectFilter from '@/components/Admin/ChannelSelectFilter';
import { Modal, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { BsEye, BsPencil } from 'react-icons/bs';
import { FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getTeamIdFromURL } from '@/_helpers';
import { FullViewLoadingSpinner } from '@/_elements/Loaders';
import AutoExpandingTextarea from '@/_elements/Text/AutoExpandingTextarea';
import { css } from 'twin.macro';

const getEmptyFormState = (teamDetails) => ({
    title: '',
    companyName: teamDetails?.name || '',
    location: '',
    employmentType: '',
    summary: '',
    responsibilities: '',
    requirements: '',
    skills: '',
    education: '',
    workExperience: '',
    contact: '',
    chatroom: '',
    payRangeMin: '0',
    payRangeMax: '0',
    payRangePeriod: '',
    payRangeDisplayText: '$7-24/hour',
});

const employmentTypeOptions = [
    { text: 'Full-time', value: 'full-time' },
    { text: 'Part-time', value: 'part-time' },
    { text: 'Contract', value: 'contract' },
    { text: 'Temporary', value: 'temporary' },
    { text: 'Internship', value: 'internship' },
];

const formatNumberValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    return `${value}`;
};

function RequiredStar() {
    return (
        <span
            css={css`
                color: red;
                margin-left: 3px;
            `}
        >
            *
        </span>
    );
}
const buildFormStateFromData = (data = {}) => ({
    chatroom: data.chatroom || '',
    title: data.title || '',
    companyName: data.companyName || '',
    location: data.location || '',
    employmentType: data.employmentType || '',
    summary: data.summary || '',
    responsibilities: data.responsibilities || '',
    requirements: data.requirements || '',
    skills: data.skills || '',
    education: data.education || '',
    workExperience: data.workExperience || '',
    contact: data.contact || '',
    payRangeMin: formatNumberValue(data.payRange?.min),
    payRangeMax: formatNumberValue(data.payRange?.max),
    payRangePeriod: data.payRange?.period || '',
    payRangeDisplayText: data?.payRange?.previewText || '',
});

export default function JobPostForm({
    teamDetails = {},
    viewMode,
    mode,
    item = null,
    onEdit = () => {},
    onCreate = () => {},
}) {
    const [formData, setFormData] = useState(getEmptyFormState());
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [selectedRoomOptions, setSelectedRoomOptions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);
    const teamId = getTeamIdFromURL();
    const chatroomTeamIds = useMemo(() => {
        if (teamId) {
            return [teamId];
        }
        return undefined;
    }, [teamId]);

    const resetFormState = () => {
        setFormData(getEmptyFormState(teamDetails));
        setSelectedRoomOptions(null);
    };

    const fetchJobPostDetails = async (jobPost) => {
        try {
            setLoading(true);
            const response = await jobPostApi.getJobPostById(jobPost._id);
            setFormData((prev) => ({
                ...prev,
                ...buildFormStateFromData(response),
                chatroom: response.chatroom || '',
            }));
            const chatroomOption = response.chatroomData
                ? {
                      label: response.chatroomData?.title,
                      value: response.chatroom,
                  }
                : null;
            setSelectedRoomOptions(chatroomOption);
        } catch (error) {
            if (error?.name !== 'AbortError') {
                toast.error(error?.message || 'Failed to load job post');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        setFormErrors({});
        if (item) {
            fetchJobPostDetails(item);
        } else {
            resetFormState();
        }
    }, [show, item]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;
        setFormData((prev) => ({
            ...prev,
            [name]: fieldValue,
        }));
        setFormErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleChatroomChange = (option) => {
        setSelectedRoomOptions(option);
        setFormData((prev) => ({
            ...prev,
            chatroom: option?.value || '',
        }));
    };

    const validateJobPostForm = (data) => {
        const errors = {};
        if (!data.title) {
            errors.title = 'Job title is required';
        }
        if (!data.companyName) {
            errors.companyName = 'Company name is required';
        }
        if (!data.chatroom) {
            errors.chatroom = 'Channel is required';
        }
        return errors;
    };

    const handleSubmit = async () => {
        try {
            setFormErrors({});
            const preparedFormData = {
                title: (formData.title || '').trim(),
                companyName: (formData.companyName || '').trim(),
                location: (formData.location || '').trim(),
                employmentType: (formData.employmentType || '').trim(),
                chatroom: (formData.chatroom || '').trim(),
                payRange: {
                    min: Number(formData.payRangeMin || 0),
                    max: Number(formData.payRangeMax || 0),
                    period: (formData.payRangePeriod || '').trim(),
                    previewText: (formData.payRangeDisplayText || '').trim(),
                },
                summary: (formData.summary || '').trim(),
                responsibilities: (formData.responsibilities || '').trim(),
                requirements: (formData.requirements || '').trim(),
                skills: (formData.skills || '').trim(),
                education: (formData.education || '').trim(),
                workExperience: (formData.workExperience || '').trim(),
                contact: (formData.contact || '').trim(),
            };

            const errors = validateJobPostForm(preparedFormData);
            if (Object.keys(errors).length) {
                setFormErrors(errors);
                return toast.error(JSON.stringify(errors));
            }

            setSubmitting(true);
            let response;
            if (item) {
                response = await jobPostApi.updateJobPost(item._id, preparedFormData);
                toast.success('Job post updated successfully!');
                onEdit(response);
            } else {
                response = await jobPostApi.createJobPost(preparedFormData);
                toast.success('Job post created successfully!');
                onCreate(response);
            }
            handleClose();
        } catch (error) {
            toast.error(error?.message || 'Something went wrong!');
        } finally {
            setSubmitting(false);
        }
    };

    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);

    return (
        <Fragment>
            {viewMode ? (
                <BootstrapToolTip text="View">
                    <BsEye className="icon view" onClick={handleShow} />
                </BootstrapToolTip>
            ) : (
                <Fragment>
                    {item ? (
                        <BootstrapToolTip text="Edit">
                            <BsPencil className="icon edit" onClick={handleShow} />
                        </BootstrapToolTip>
                    ) : (
                        <Button variant="primary" onClick={handleShow}>
                            <FaPlus className="mr-1" /> New Job Post
                        </Button>
                    )}
                </Fragment>
            )}

            <Modal show={show} size="lg" backdrop="static">
                <Modal.Header>
                    <Modal.Title>
                        {viewMode ? (
                            'View Job Post'
                        ) : item ? (
                            <strong>Edit Job Post</strong>
                        ) : (
                            <strong>Create Job Post</strong>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <FullViewLoadingSpinner />
                    ) : (
                        <Fragment>
                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Job title</strong>
                                    <RequiredStar />
                                </Form.Label>
                                <Form.Control
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="e.g. Direct Support Professional"
                                    disabled={viewMode}
                                />
                                <Form.Text className="text-danger">{formErrors.title}</Form.Text>
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Company name</strong>
                                    <RequiredStar />
                                </Form.Label>
                                <Form.Control
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    disabled={viewMode}
                                />
                                <Form.Text className="text-danger">{formErrors.companyName}</Form.Text>
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Location</strong>
                                </Form.Label>

                                <Form.Control
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    placeholder="City, state or remote"
                                    disabled={viewMode}
                                />
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Employment type</strong>
                                </Form.Label>

                                <Form.Control
                                    as="select"
                                    name="employmentType"
                                    value={formData.employmentType}
                                    onChange={handleChange}
                                    disabled={viewMode}
                                >
                                    <option value="">Not Selected</option>
                                    {employmentTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            {/* <hr /> */}
                            {/* <strong>Payrange Information:</strong> */}
                            <Row>
                                {/* <Col>
                                    <Form.Group>
                                        <Form.Label>Pay range (min)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            name="payRangeMin"
                                            value={formData.payRangeMin}
                                            onChange={handleChange}
                                            disabled={viewMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Pay range (max)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            name="payRangeMax"
                                            value={formData.payRangeMax}
                                            onChange={handleChange}
                                            disabled={viewMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Pay period</Form.Label>
                                        <Form.Control
                                            name="payRangePeriod"
                                            value={formData.payRangePeriod}
                                            onChange={handleChange}
                                            disabled={viewMode}
                                        />
                                    </Form.Group>
                                </Col> */}
                                <Col>
                                    <Form.Group>
                                        <Form.Label>
                                            <strong>Pay Range</strong>
                                        </Form.Label>
                                        <Form.Control
                                            name="payRangeDisplayText"
                                            value={formData.payRangeDisplayText}
                                            onChange={handleChange}
                                            disabled={viewMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <hr />
                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Skills</strong>
                                </Form.Label>

                                <Form.Control
                                    name="skills"
                                    value={formData.skills}
                                    onChange={handleChange}
                                    placeholder=""
                                    disabled={viewMode}
                                />
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Education</strong>
                                </Form.Label>

                                <Form.Control
                                    name="education"
                                    value={formData.education}
                                    onChange={handleChange}
                                    placeholder=""
                                    disabled={viewMode}
                                />
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Work Experience</strong>
                                </Form.Label>

                                <Form.Control
                                    name="workExperience"
                                    value={formData.workExperience}
                                    onChange={handleChange}
                                    placeholder=""
                                    disabled={viewMode}
                                />
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Summary (Short summary of the role):</strong>
                                </Form.Label>
                                <AutoExpandingTextarea
                                    name="summary"
                                    value={formData.summary}
                                    onChange={handleChange}
                                    disabled={viewMode}
                                    minRows={2}
                                />
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Responsibilities (bullets or sentences):</strong>
                                </Form.Label>
                                <AutoExpandingTextarea
                                    minRows={2}
                                    name="responsibilities"
                                    value={formData.responsibilities}
                                    onChange={handleChange}
                                    disabled={viewMode}
                                />
                            </Form.Group>

                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Requirements (bullets or sentences):</strong>
                                </Form.Label>

                                <AutoExpandingTextarea
                                    minRows={2}
                                    name="requirements"
                                    value={formData.requirements}
                                    onChange={handleChange}
                                    disabled={viewMode}
                                />
                            </Form.Group>
                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Contact Information:</strong>
                                </Form.Label>

                                <AutoExpandingTextarea
                                    minRows={2}
                                    name="contact"
                                    value={formData.contact}
                                    onChange={handleChange}
                                    disabled={viewMode}
                                />
                            </Form.Group>
                            <Form.Group classtitle="mb-4">
                                <Form.Label>
                                    <strong>Channel:</strong>
                                    <RequiredStar />
                                </Form.Label>
                                <ChannelSelectFilter
                                    teamIds={chatroomTeamIds}
                                    selectedOption={selectedRoomOptions}
                                    onRoomSelect={handleChatroomChange}
                                    disabled={viewMode}
                                />
                                <Form.Text className="text-danger">{formErrors.chatroom}</Form.Text>
                            </Form.Group>
                        </Fragment>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Close
                    </Button>
                    {viewMode ? null : (
                        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        classtitle="mr-1"
                                    />
                                    Saving...
                                </>
                            ) : item ? (
                                'Update'
                            ) : (
                                'Create'
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
}
